import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/DetailedSaleForm.css';
import { jsPDF } from 'jspdf';

const SupervisorDetailedSaleForm = ({ onSaleAdded, onCancel }) => {
  const { user } = useAuth();
  const [businessDateTime, setBusinessDateTime] = useState(null);
  const [formData, setFormData] = useState({
    sale_date: new Date().toISOString().split('T')[0],
    total_amount: '',
    payment_method: 'Efectivo',
    point_of_sale_id: '',
    customer_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [productQuantity, setProductQuantity] = useState('');
  const [productDiscount, setProductDiscount] = useState(0.00);
  const [generalDiscount, setGeneralDiscount] = useState(0.00);
  const [taxRate, setTaxRate] = useState(0.00);
  const [taxAmount, setTaxAmount] = useState(0.00);
  const [subtotal, setSubtotal] = useState(0.00);
  const [totalBeforeTax, setTotalBeforeTax] = useState(0.00);
  const [grandTotal, setGrandTotal] = useState(0.00);
  const [loadingPointsOfSale, setLoadingPointsOfSale] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [printTicket, setPrintTicket] = useState(false);
  const [isGeneratingTicket, setIsGeneratingTicket] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Función para obtener la fecha y hora del negocio desde el servidor
  const fetchBusinessTime = async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No se encontró token de autenticación');
        return null;
      }

      const response = await axios.get(`/api/sales/server-time?business_id=${businessId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 5000
      });

      if (response.data && response.data.time) {
        setBusinessDateTime(response.data.time);
        return response.data.time;
      } else {
        console.warn('La respuesta del servidor no contiene fecha/hora');
        return null;
      }
    } catch (error) {
      console.error('Error al obtener fecha/hora del negocio:', error);
      return null;
    }
  };

  // Función para generar el ticket de venta detallada en PDF
  const generateTicket = async (saleData) => {
    try {
      console.log('Iniciando generación de ticket PDF para venta detallada del supervisor...');
      setIsGeneratingTicket(true);
      let formattedDate = '';
      let formattedTime = '';
      let businessTimezone = 'America/Mexico_City';
      
      // Obtener el business_id del punto de venta seleccionado
      let businessId = null;
      const selectedPointOfSale = pointsOfSale.find(pos => pos.id === formData.point_of_sale_id);
      if (selectedPointOfSale && selectedPointOfSale.business_id) {
        businessId = selectedPointOfSale.business_id;
      }
      
      // Intentar obtener la hora del servidor
      try {
        const serverTimeResponse = await axios.get('/api/sales/server-time', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 5000
        });
        
        const serverTimeStr = serverTimeResponse.data.time;
        businessTimezone = serverTimeResponse.data.timezone || 'America/Mexico_City';
        
        if (serverTimeStr && typeof serverTimeStr === 'string') {
          const dateParts = serverTimeStr.split('T');
          if (dateParts.length === 2) {
            const rawDate = dateParts[0];
            const dateComponents = rawDate.split('-');
            if (dateComponents.length === 3) {
              formattedDate = `${dateComponents[2]}/${dateComponents[1]}/${dateComponents[0]}`;
              const rawTime = dateParts[1].split('.')[0];
              formattedTime = rawTime;
            }
          }
        }
      } catch (serverTimeError) {
        console.error('Error al obtener la hora del servidor:', serverTimeError);
        const now = new Date();
        formattedDate = now.toLocaleDateString('es-MX', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        formattedTime = now.toLocaleTimeString('es-MX', { 
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false 
        });
      }
      
      // Obtener datos del negocio y usuario
      let business = {};
      let currentUser = {};
      
      if (selectedPointOfSale && selectedPointOfSale.business_id) {
        try {
          const businessDetailResponse = await axios.get(`/api/businesses/${selectedPointOfSale.business_id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 5000
          });
          
          if (businessDetailResponse.data) {
            business = businessDetailResponse.data;
          }
        } catch (businessError) {
          console.error('Error al obtener datos del negocio:', businessError);
        }
      }
      
      try {
        const userResponse = await axios.get('/api/users/me', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 3000
        });
        currentUser = userResponse.data;
      } catch (userError) {
        console.error('Error al obtener datos del usuario:', userError);
        currentUser = { full_name: 'Supervisor' };
      }
      
      // Crear el documento PDF con ancho de 80mm (ticket)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [80, 300]
      });
      
      doc.setFont('helvetica');
      doc.setFontSize(10);
      
      // Encabezado del ticket
      doc.text(business.bis_name || 'Negocio', 40, 10, null, null, 'center');
      doc.text('Ticket de Venta', 40, 15, null, null, 'center');
      doc.text(`Tel: ${business.bis_phone || 'No disponible'}`, 40, 20, null, null, 'center');
      doc.text('Venta Detallada - Supervisor', 40, 25, null, null, 'center');
      
      // Información de la venta
      const displayPosName = selectedPointOfSale ? selectedPointOfSale.pos_name : 'No disponible';
      
      doc.text(`Punto de venta: ${displayPosName}`, 2, 35);
      const userName = currentUser && currentUser.full_name ? currentUser.full_name.substring(0, 20) : 'No disponible';
      doc.text(`Supervisor: ${userName}`, 2, 40);
      doc.text(`Fecha: ${formattedDate || 'No disponible'}`, 2, 45);
      doc.text(`Hora: ${formattedTime || 'No disponible'} (${businessTimezone || 'UTC'})`, 2, 50);
      
      // Línea divisoria
      doc.line(2, 55, 78, 55);
      doc.setFontSize(9);
      doc.text('Detalle de la venta', 40, 60, null, null, 'center');
      doc.setFontSize(8);
      doc.text('Producto', 2, 65);
      doc.text('Cant.', 45, 65, null, null, 'center');
      doc.text('Subtotal', 75, 65, null, null, 'right');
      
      // Línea divisoria
      doc.line(2, 70, 78, 70);
      
      // Detalles de los productos
      let y = 75;
      saleData.products.forEach(product => {
        const productName = product.name || product.prod_name || 'Producto';
        const displayName = productName.length > 25 ? productName.substring(0, 22) + '...' : productName;
        const subtotal = product.subtotal || (product.price * product.quantity) - (product.discount || 0);
        
        doc.text(displayName, 2, y);
        doc.text(product.quantity.toString(), 45, y, null, null, 'center');
        doc.text(`$${subtotal.toFixed(2)}`, 75, y, null, null, 'right');
        y += 5;
      });
      
      // Línea divisoria
      doc.line(2, y, 78, y);
      y += 5;
      
      // Totales
      const subtotalValue = typeof saleData.subtotal === 'number' ? saleData.subtotal : 0;
      doc.text(`Subtotal: $${subtotalValue.toFixed(2)}`, 75, y, null, null, 'right');
      y += 5;
      
      let generalDiscountValue = 0;
      if (typeof saleData.generalDiscount === 'number') {
        generalDiscountValue = saleData.generalDiscount;
      } else if (typeof saleData.generalDiscount === 'string' && saleData.generalDiscount !== '' && saleData.generalDiscount !== '.') {
        generalDiscountValue = parseFloat(saleData.generalDiscount);
      } else if (typeof saleData.general_discount === 'number') {
        generalDiscountValue = saleData.general_discount;
      } else if (typeof saleData.general_discount === 'string' && saleData.general_discount !== '' && saleData.general_discount !== '.') {
        generalDiscountValue = parseFloat(saleData.general_discount);
      }
      
      if (isNaN(generalDiscountValue)) generalDiscountValue = 0;
      
      doc.text(`Descuento: $${generalDiscountValue.toFixed(2)}`, 75, y, null, null, 'right');
      y += 5;
      
      const totalBeforeTaxValue = typeof saleData.totalBeforeTax === 'number' ? saleData.totalBeforeTax : 
                                (typeof saleData.total_before_tax === 'number' ? saleData.total_before_tax : subtotalValue - generalDiscountValue);
      doc.text(`Total antes de impuesto: $${totalBeforeTaxValue.toFixed(2)}`, 75, y, null, null, 'right');
      y += 5;
      
      const taxAmountValue = typeof saleData.taxAmount === 'number' ? saleData.taxAmount : 
                          (typeof saleData.tax_amount === 'number' ? saleData.tax_amount : 0);
      const taxRateValue = typeof saleData.taxRate === 'number' ? saleData.taxRate : 
                         (typeof saleData.tax_rate === 'number' ? saleData.tax_rate : 0);
      
      if (taxAmountValue > 0) {
        doc.text(`Impuesto (${taxRateValue.toFixed(2)}%): $${taxAmountValue.toFixed(2)}`, 75, y, null, null, 'right');
        y += 5;
      }
      
      // Gran total
      doc.setFontSize(10);
      const grandTotalValue = typeof saleData.grandTotal === 'number' ? saleData.grandTotal : 
                             (typeof saleData.grand_total === 'number' ? saleData.grand_total : 0);
      doc.text(`Gran Total: $${grandTotalValue.toFixed(2)}`, 75, y, null, null, 'right');
      doc.setFontSize(8);
      y += 10;
      
      // Método de pago
      const paymentMethod = saleData.paymentMethod || saleData.payment_method || 'Efectivo';
      doc.text(`Método de pago: ${paymentMethod}`, 2, y);
      y += 10;
      
      // Pie de página
      doc.text('Gracias por su compra', 40, y, null, null, 'center');
      y += 5;
      
      // Línea en blanco
      y += 5;
      
      // ID de venta
      doc.text('IdVenta', 40, y, null, null, 'center');
      y += 5;
      
      const dateForId = formattedDate.replace(/\//g, '');
      const timeForId = formattedTime.replace(/:/g, '').replace(/\s/g, '');
      const posName = (selectedPointOfSale?.pos_name || 'POS').replace(/\s+/g, '');
      const supervisorName = (currentUser?.full_name || 'Supervisor').substring(0, 12).replace(/\s+/g, '');
      const amountForId = Math.floor(grandTotalValue).toString();
      
      const ventaId = `${dateForId}${timeForId}${posName}${supervisorName}${amountForId}`;
      doc.text(ventaId, 40, y, null, null, 'center');
      y += 5;
      
      // Generar el nombre del archivo con la fecha y hora
      const dateForFilename = formattedDate.replace(/\//g, '-');
      const timeForFilename = formattedTime.replace(/:/g, '-').replace(/\s/g, '');
      const filename = `ticket_venta_detallada_supervisor_${dateForFilename}_${timeForFilename}.pdf`;
      
      // Método directo de jsPDF
      try {
        doc.save(filename);
        setIsGeneratingTicket(false);
        return true;
      } catch (saveError) {
        console.error('Error con método directo:', saveError);
        
        try {
          const blob = doc.output('blob');
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.style.display = 'none';
          document.body.appendChild(link);
          
          link.click();
          
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, 1000);
          
          setIsGeneratingTicket(false);
          return true;
        } catch (blobError) {
          console.error('Error con método Blob:', blobError);
          
          try {
            const base64 = doc.output('datauristring');
            window.open(base64);
            setIsGeneratingTicket(false);
            return true;
          } catch (base64Error) {
            console.error('Error con método base64:', base64Error);
          }
        }
      }
      
      setIsGeneratingTicket(false);
      return false;
    } catch (error) {
      console.error('Error al generar el ticket:', error);
      setIsGeneratingTicket(false);
      return false;
    }
  };

  useEffect(() => {
    const fetchPointsOfSale = async () => {
      try {
        setLoadingPointsOfSale(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get('/api/supervisors/points-of-sale', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setPointsOfSale(response.data);
        
        if (response.data.length === 1) {
          setFormData(prevState => ({
            ...prevState,
            point_of_sale_id: response.data[0].id
          }));
        } else if (response.data.length > 0) {
          setFormData(prevState => ({
            ...prevState,
            point_of_sale_id: response.data[0].id
          }));
          
          if (response.data[0].business_id) {
            fetchBusinessTime(response.data[0].business_id);
          }
        }
      } catch (error) {
        console.error('Error al obtener puntos de venta:', error);
        setError('Error al cargar los puntos de venta. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingPointsOfSale(false);
      }
    };

    const fetchCustomers = async () => {
      try {
        setLoadingCustomers(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get('/api/customers', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        const activeCustomers = response.data.filter(customer => customer.cus_alive);
        setCustomers(activeCustomers);
        
        if (activeCustomers.length > 0) {
          const publicCustomer = activeCustomers.find(c => c.cus_name === 'Publico en General');
          if (publicCustomer) {
            setFormData(prevState => ({
              ...prevState,
              customer_id: publicCustomer.id
            }));
          } else {
            setFormData(prevState => ({
              ...prevState,
              customer_id: activeCustomers[0].id
            }));
          }
        }
      } catch (error) {
        console.error('Error al obtener clientes:', error);
        setError('Error al cargar los clientes. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingCustomers(false);
      }
    };

    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get('/api/products', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        const activeProducts = response.data.filter(product => product.prod_alive);
        activeProducts.sort((a, b) => a.prod_name.localeCompare(b.prod_name));
        
        setProducts(activeProducts);
        setFilteredProducts(activeProducts);
        
        if (activeProducts.length > 0) {
          setSelectedProductId(activeProducts[0].id);
        }
      } catch (error) {
        console.error('Error al obtener productos:', error);
        setError('Error al cargar los productos. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchPointsOfSale();
    fetchCustomers();
    fetchProducts();
  }, [user]);

  // Calcular el subtotal basado en los productos seleccionados
  useEffect(() => {
    let calculatedSubtotal = 0;
    selectedProducts.forEach(product => {
      const discountValue = typeof product.discount === 'string' ?
        (product.discount === '' || product.discount === '.' ? 0 : parseFloat(product.discount)) :
        product.discount;
      
      const productSubtotal = (product.prod_price * product.quantity) - discountValue;
      calculatedSubtotal += productSubtotal;
    });
    
    setSubtotal(calculatedSubtotal);
  }, [selectedProducts]);

  // Efecto para filtrar productos cuando cambia el término de búsqueda
  useEffect(() => {
    if (!productSearchTerm.trim()) {
      setFilteredProducts(products);
    } else {
      const searchTermLower = productSearchTerm.toLowerCase().trim();
      const filtered = products.filter(product => 
        (product.prod_code && product.prod_code.toLowerCase().includes(searchTermLower)) || 
        product.prod_name.toLowerCase().includes(searchTermLower)
      );
      setFilteredProducts(filtered);
      
      if (filtered.length > 0) {
        setSelectedProductId(filtered[0].id);
      }
    }
  }, [productSearchTerm, products]);

  // Calcular el gran total cuando cambia el subtotal, descuento general o impuesto
  useEffect(() => {
    const discountValue = typeof generalDiscount === 'string' ?
      (generalDiscount === '' || generalDiscount === '.' ? 0 : parseFloat(generalDiscount)) :
      generalDiscount;
    
    const afterDiscount = Math.max(0, subtotal - discountValue);
    setTotalBeforeTax(afterDiscount);
    
    const taxAmount = afterDiscount * (taxRate / 100);
    const calculatedGrandTotal = afterDiscount + taxAmount;
    
    setGrandTotal(calculatedGrandTotal);
    setTaxAmount(taxAmount);
    
    setFormData(prevState => ({
      ...prevState,
      total_amount: calculatedGrandTotal.toFixed(2)
    }));
  }, [subtotal, generalDiscount, taxRate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleProductChange = (e) => {
    setSelectedProductId(e.target.value);
  };

  const handleProductSearchChange = (e) => {
    setProductSearchTerm(e.target.value);
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    if (value === '' || value === '.') {
      setProductQuantity(value);
      return;
    }
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value)) {
      setProductQuantity(value);
    }
  };

  const handleDiscountChange = (e) => {
    let value = e.target.value;
    if (value === '' || value === '.') {
      setProductDiscount(value);
      return;
    }
    if (/^\d*\.?\d*$/.test(value)) {
      setProductDiscount(value);
    }
  };

  const handleGeneralDiscountChange = (e) => {
    let value = e.target.value;
    if (value === '' || value === '.') {
      setGeneralDiscount(value);
      return;
    }
    if (/^\d*\.?\d*$/.test(value)) {
      setGeneralDiscount(value);
    }
  };

  const handleTaxRateChange = (e) => {
    const value = parseFloat(e.target.value);
    if (value >= 0) {
      setTaxRate(value);
    }
  };

  const handleAddProduct = () => {
    if (!selectedProductId) {
      setError('Por favor, seleccione un producto.');
      return;
    }

    if (productQuantity === '' || productQuantity === '.') {
      setError('Por favor, ingrese una cantidad válida.');
      return;
    }

    const quantityValue = parseFloat(productQuantity);
    if (isNaN(quantityValue) || quantityValue <= 0) {
      setError('La cantidad debe ser mayor a 0.');
      return;
    }

    const product = products.find(p => p.id.toString() === selectedProductId.toString());
    if (!product) {
      setError('Producto no encontrado.');
      return;
    }

    const discountValue = productDiscount === '' || productDiscount === '.' ? 
      0 : parseFloat(productDiscount);

    const existingProductIndex = selectedProducts.findIndex(
      p => p.id.toString() === selectedProductId.toString()
    );

    if (existingProductIndex >= 0) {
      const updatedProducts = [...selectedProducts];
      updatedProducts[existingProductIndex].quantity = parseFloat(updatedProducts[existingProductIndex].quantity) + parseFloat(productQuantity);
      updatedProducts[existingProductIndex].discount = discountValue;
      setSelectedProducts(updatedProducts);
    } else {
      setSelectedProducts([
        ...selectedProducts,
        {
          ...product,
          quantity: parseFloat(productQuantity),
          discount: discountValue
        }
      ]);
    }

    setSelectedProductId('');
    setProductQuantity('');
    setProductDiscount('');
    setError('');
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      return;
    }
    
    const updatedProducts = selectedProducts.map(product => {
      if (product.id.toString() === productId.toString()) {
        return {
          ...product,
          quantity: parseFloat(newQuantity)
        };
      }
      return product;
    });
    
    setSelectedProducts(updatedProducts);
  };

  const handleUpdateDiscount = (productId, newDiscount) => {
    if (newDiscount === '' || newDiscount === '.') {
      const updatedProducts = selectedProducts.map(product => {
        if (product.id.toString() === productId.toString()) {
          return {
            ...product,
            discount: newDiscount
          };
        }
        return product;
      });
      
      setSelectedProducts(updatedProducts);
      return;
    }
    
    const discountValue = parseFloat(newDiscount);
    if (isNaN(discountValue) || discountValue < 0) {
      return;
    }
    
    const updatedProducts = selectedProducts.map(product => {
      if (product.id.toString() === productId.toString()) {
        return {
          ...product,
          discount: newDiscount
        };
      }
      return product;
    });
    
    setSelectedProducts(updatedProducts);
  };

  const handleRemoveProduct = (productId) => {
    const updatedProducts = selectedProducts.filter(
      product => product.id.toString() !== productId.toString()
    );
    
    setSelectedProducts(updatedProducts);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
        return;
      }
      
      let currentBusinessTime = businessDateTime;
      const selectedPointOfSale = pointsOfSale.find(pos => pos.id === formData.point_of_sale_id);
      if (selectedPointOfSale?.business_id) {
        const freshBusinessTime = await fetchBusinessTime(selectedPointOfSale.business_id);
        if (freshBusinessTime) {
          currentBusinessTime = freshBusinessTime;
        }
      }

      if (!formData.sale_date || !formData.total_amount || !formData.point_of_sale_id || !formData.payment_method || !formData.customer_id) {
        setError('Todos los campos son obligatorios');
        setLoading(false);
        return;
      }

      if (isNaN(formData.total_amount) || parseFloat(formData.total_amount) <= 0) {
        setError('El monto total debe ser un número positivo');
        setLoading(false);
        return;
      }

      if (selectedProducts.length === 0) {
        setError('Debe agregar al menos un producto a la venta');
        setLoading(false);
        return;
      }

      const productDetails = selectedProducts.map(product => ({
        product_id: product.id,
        quantity: product.quantity,
        price: product.prod_price,
        discount: product.discount,
        subtotal: (product.prod_price * product.quantity) - product.discount
      }));

      let finalSaleDate = formData.sale_date;
      
      try {
        if (currentBusinessTime && formData.sale_date) {
          const [userYear, userMonth, userDay] = formData.sale_date.split('-').map(Number);
          const businessDate = new Date(currentBusinessTime);
          
          const combinedDate = new Date(Date.UTC(
            userYear,
            userMonth - 1,
            userDay,
            businessDate.getUTCHours(),
            businessDate.getUTCMinutes(),
            businessDate.getUTCSeconds(),
            businessDate.getUTCMilliseconds()
          ));
          
          finalSaleDate = combinedDate.toISOString();
        }
      } catch (dateError) {
        console.error('Error al combinar fecha del usuario con zona horaria del negocio:', dateError);
      }

      const response = await axios.post('/api/supervisors/detailed-sale', 
        {
          sale_date: finalSaleDate,
          total_amount: parseFloat(formData.total_amount),
          payment_method: formData.payment_method,
          point_of_sale_id: formData.point_of_sale_id,
          customer_id: formData.customer_id,
          products: productDetails,
          general_discount: generalDiscount,
          tax_rate: taxRate,
          subtotal: subtotal,
          grand_total: grandTotal
        }, 
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setSuccessMessage('Venta registrada correctamente');
      
      if (printTicket) {
        const ticketData = {
          sale_date: finalSaleDate,
          payment_method: formData.payment_method,
          point_of_sale_id: formData.point_of_sale_id,
          products: selectedProducts.map(product => ({
            id: product.id,
            name: product.prod_name,
            quantity: product.quantity,
            price: product.prod_price,
            discount: product.discount || 0,
            subtotal: (product.prod_price * product.quantity) - (product.discount || 0)
          })),
          generalDiscount: typeof generalDiscount === 'string' ? 
            (generalDiscount === '' || generalDiscount === '.' ? 0 : parseFloat(generalDiscount)) : 
            (typeof generalDiscount === 'number' ? generalDiscount : 0),
          general_discount: typeof generalDiscount === 'string' ? 
            (generalDiscount === '' || generalDiscount === '.' ? 0 : parseFloat(generalDiscount)) : 
            (typeof generalDiscount === 'number' ? generalDiscount : 0),
          taxRate: taxRate,
          tax_rate: taxRate,
          taxAmount: taxAmount,
          tax_amount: taxAmount,
          subtotal: subtotal,
          totalBeforeTax: totalBeforeTax,
          total_before_tax: totalBeforeTax,
          grandTotal: grandTotal,
          grand_total: grandTotal
        };
        
        const ticketGenerated = await generateTicket(ticketData);
      }

      setFormData({
        sale_date: new Date().toISOString().split('T')[0],
        total_amount: '',
        payment_method: 'Efectivo',
        point_of_sale_id: formData.point_of_sale_id,
        customer_id: formData.customer_id
      });
      setSelectedProducts([]);
      setGeneralDiscount(0.00);
      setTaxRate(0.00);
      setTaxAmount(0.00);
      setPrintTicket(false);
      
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

      onSaleAdded();
    } catch (error) {
      console.error('Error al registrar la venta:', error);
      setError(error.response?.data?.message || 'Error al registrar la venta. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="detailed-sale-form-container">
      <h3>Venta Detallada - Supervisor</h3>
      {successMessage && <div className="success-message" style={{ backgroundColor: '#d4edda', color: '#155724', padding: '10px', borderRadius: '4px', marginBottom: '15px' }}>{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}
      <form className="detailed-sale-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="point_of_sale_id">Punto de Venta *</label>
          <select
            id="point_of_sale_id"
            name="point_of_sale_id"
            value={formData.point_of_sale_id}
            onChange={handleChange}
            required
            disabled={loadingPointsOfSale}
          >
            <option value="">
              {loadingPointsOfSale ? 'Cargando...' : 'Seleccione un punto de venta'}
            </option>
            {pointsOfSale.map(pos => (
              <option key={pos.id} value={pos.id}>
                {pos.pos_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="customer_id">Cliente *</label>
          <select
            id="customer_id"
            name="customer_id"
            value={formData.customer_id}
            onChange={handleChange}
            required
            disabled={loadingCustomers}
          >
            <option value="">
              {loadingCustomers ? 'Cargando...' : 'Seleccione un cliente'}
            </option>
            {customers.map(customer => (
              <option key={customer.id} value={customer.id}>
                {customer.cus_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="sale_date">Fecha de Venta *</label>
          <input
            type="date"
            id="sale_date"
            name="sale_date"
            value={formData.sale_date}
            onChange={handleChange}
            required
          />
        </div>

        <div className="products-section">
          <h4>Productos</h4>
          
          <div className="add-product-form">
            <div className="product-search">
              <input
                type="text"
                className="product-search-input"
                placeholder="Buscar producto por código o nombre..."
                value={productSearchTerm}
                onChange={handleProductSearchChange}
              />
            </div>

            <div className="product-selection">
              <select
                value={selectedProductId}
                onChange={handleProductChange}
                disabled={loadingProducts}
              >
                <option value="">
                  {loadingProducts ? 'Cargando productos...' : 'Seleccione un producto'}
                </option>
                {filteredProducts.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.prod_code ? `${product.prod_code} - ` : ''}{product.prod_name} - $${product.prod_price}
                  </option>
                ))}
              </select>

              <div className="quantity-input">
                <label>Cantidad</label>
                <input
                  type="text"
                  placeholder="1"
                  value={productQuantity}
                  onChange={handleQuantityChange}
                />
              </div>

              <div className="discount-input">
                <label>Descuento</label>
                <input
                  type="text"
                  placeholder="0.00"
                  value={productDiscount}
                  onChange={handleDiscountChange}
                />
              </div>

              <button type="button" onClick={handleAddProduct} className="add-product-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Agregar
              </button>
            </div>
          </div>

          {selectedProducts.length > 0 ? (
            <table className="products-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Precio</th>
                  <th>Cantidad</th>
                  <th>Descuento</th>
                  <th>Subtotal</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {selectedProducts.map(product => {
                  const discountValue = typeof product.discount === 'string' ?
                    (product.discount === '' || product.discount === '.' ? 0 : parseFloat(product.discount)) :
                    product.discount;
                  const productSubtotal = (product.prod_price * product.quantity) - discountValue;
                  
                  return (
                    <tr key={product.id}>
                      <td>{product.prod_name}</td>
                      <td>${product.prod_price.toFixed(2)}</td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={product.quantity}
                          onChange={(e) => handleUpdateQuantity(product.id, e.target.value)}
                          style={{ width: '80px', textAlign: 'center' }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={product.discount}
                          onChange={(e) => handleUpdateDiscount(product.id, e.target.value)}
                          className="discount-input-field"
                          placeholder="0.00"
                        />
                      </td>
                      <td>${productSubtotal.toFixed(2)}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleRemoveProduct(product.id)}
                          className="remove-product-btn"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4" className="total-label">Subtotal:</td>
                  <td className="total-amount">${subtotal.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <div className="no-products-message">
              No hay productos agregados a la venta
            </div>
          )}
        </div>

        <div className="totals-section">
          <div className="totals-grid">
            <div className="total-row">
              <span className="total-label">Descuento General:</span>
              <div className="total-input">
                <input
                  type="text"
                  placeholder="0.00"
                  value={generalDiscount}
                  onChange={handleGeneralDiscountChange}
                />
              </div>
            </div>

            <div className="total-row">
              <span className="total-label">Tasa de Impuesto (%):</span>
              <div className="total-input">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={taxRate}
                  onChange={handleTaxRateChange}
                />
              </div>
            </div>

            <div className="total-row">
              <span className="total-label">Subtotal:</span>
              <span className="total-value">${subtotal.toFixed(2)}</span>
            </div>

            <div className="total-row">
              <span className="total-label">Descuento General:</span>
              <span className="total-value">-${(typeof generalDiscount === 'string' ? 
                (generalDiscount === '' || generalDiscount === '.' ? 0 : parseFloat(generalDiscount)) : 
                generalDiscount).toFixed(2)}</span>
            </div>

            <div className="total-row">
              <span className="total-label">Total antes de impuesto:</span>
              <span className="total-value">${totalBeforeTax.toFixed(2)}</span>
            </div>

            <div className="total-row">
              <span className="total-label">Impuesto ({taxRate.toFixed(2)}%):</span>
              <span className="total-value">${taxAmount.toFixed(2)}</span>
            </div>

            <div className="total-row grand-total">
              <span className="total-label">Gran Total:</span>
              <span className="total-value">${grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Método de Pago *</label>
          <div className="payment-method-options">
            <div className={`payment-option ${formData.payment_method === 'Efectivo' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="efectivo"
                name="payment_method"
                value="Efectivo"
                checked={formData.payment_method === 'Efectivo'}
                onChange={handleChange}
              />
              <label htmlFor="efectivo">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="10" rx="2" ry="2"></rect>
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="m18.5 9.5-1 1"></path>
                  <path d="m6.5 14.5 1-1"></path>
                </svg>
                Efectivo
              </label>
            </div>

            <div className={`payment-option ${formData.payment_method === 'Tarjeta' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="tarjeta"
                name="payment_method"
                value="Tarjeta"
                checked={formData.payment_method === 'Tarjeta'}
                onChange={handleChange}
              />
              <label htmlFor="tarjeta">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                  <line x1="1" y1="10" x2="23" y2="10"></line>
                </svg>
                Tarjeta
              </label>
            </div>

            <div className={`payment-option ${formData.payment_method === 'Transferencia' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="transferencia"
                name="payment_method"
                value="Transferencia"
                checked={formData.payment_method === 'Transferencia'}
                onChange={handleChange}
              />
              <label htmlFor="transferencia">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                  <line x1="12" y1="18" x2="12.01" y2="18"></line>
                  <path d="M8 6h8"></path>
                  <path d="M8 10h8"></path>
                  <path d="M8 14h5"></path>
                </svg>
                Transferencia
              </label>
            </div>
          </div>
        </div>

        <div className="checkbox-container" style={{ marginTop: '15px', display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            id="print_ticket"
            checked={printTicket}
            onChange={(e) => setPrintTicket(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          <label htmlFor="print_ticket" className="checkbox-label" style={{ fontSize: '14px', cursor: 'pointer' }}>
            Generar Ticket de Venta (80mm)
          </label>
        </div>

        <div className="form-buttons">
          <button type="button" className="cancel-btn" onClick={onCancel} disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="submit-btn" disabled={loading || selectedProducts.length === 0}>
            {loading ? 'Registrando...' : 'Registrar Venta'}
          </button>
        </div>
      </form>

      {isGeneratingTicket && (
        <div className="generating-ticket">
          Generando ticket PDF...
        </div>
      )}
    </div>
  );
};

export default SupervisorDetailedSaleForm;
